import { Button, cn, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, Spinner } from '@1trading/react';
import { ChartLine, ChevronDown, CircleAlert, ExternalLink, FileDown, KeyRound, Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { CopyButton } from '@/components/ui/copy-button';
import { LabeledValue } from '@/components/ui/labeled-value';
import { STATUS_CHIP_SIZE_CLASSES, StatusChip } from '@/components/ui/status-chip';
import { AbuseStatusChip } from '@/features/abuse/abuse-status-chip';
import { useGdprReport } from '@/features/user-console/use-gdpr-report';
import { openPanelProfileUrl } from '@/lib/paths';
import { type ConsoleUser, isBrokerClientType, type StatusChangeAction } from '@/services/api/user-console';
import { pluralize } from '@/utils/string';
import { formatSegmentTitle, formatUserStateTitle, formatUserTypeTitle, restrictedFeatures, userHeading, userStateChipVariant } from './user-display';

// Actions whose landing state differs from the action name
const ACTION_RESULT_STATE: Partial<Record<StatusChangeAction, string>> = { REACTIVATED: 'ACTIVE', CLOSED: 'DELETED' };

interface UserActionsMenuProps {
  user: ConsoleUser;
  onEditSegment: () => void;
  onManageAccess: () => void;
}

const UserActionsMenu = ({ user, onEditSegment, onManageAccess }: UserActionsMenuProps) => {
  const { isGenerating, generateReport } = useGdprReport(user.PID ?? '');

  const handleGenerateGdprReport = async () => {
    const justification = await confirmAction({
      title: 'Generate GDPR report?',
      description: `Generate the GDPR Article-15 report for ${user.email ?? 'this user'}. Recorded in the audit log and delivered to your session when ready.`,
      actionLabel: 'Generate report',
    });
    if (justification === null) return;
    await generateReport(justification);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="shrink-0 gap-1.5" disabled={isGenerating} size="sm" type="button" variant="outline">
          {isGenerating ? <Spinner className="size-4" /> : null}
          Actions
          <ChevronDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem className="cursor-pointer gap-2" onSelect={onEditSegment}>
          <Pencil className="size-4" />
          Change segment…
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer gap-2" onSelect={onManageAccess}>
          <KeyRound className="size-4" />
          Manage access
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer gap-2" disabled={isGenerating || !user.PID} onSelect={handleGenerateGdprReport}>
          <FileDown className="size-4" />
          Generate GDPR report…
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">Open in</DropdownMenuLabel>
        <DropdownMenuItem asChild className="cursor-pointer gap-2">
          <a href={openPanelProfileUrl(user.PID)} rel="noreferrer" target="_blank">
            <ChartLine className="size-4" />
            OpenPanel profile
            <ExternalLink className="ml-auto size-3.5 text-muted-foreground" />
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
UserActionsMenu.displayName = 'UserActionsMenu';

const statusChangeNote = (user: ConsoleUser): string | null => {
  if (!user.statusChangeAction || !user.statusChangeAt) return null;
  const landedState = ACTION_RESULT_STATE[user.statusChangeAction] ?? user.statusChangeAction;
  if (landedState === user.state) return `since ${user.statusChangeAt}`;
  return `${formatUserStateTitle(user.statusChangeAction)} ${user.statusChangeAt}`;
};

interface UserStatusChipsProps {
  user: ConsoleUser;
  onEditSegment: () => void;
  onManageAccess: () => void;
}

const UserStatusChips = ({ user, onEditSegment, onManageAccess }: UserStatusChipsProps) => {
  const restricted = restrictedFeatures(user);
  const pendingCount = user.pendingItems?.length ?? 0;
  const changeNote = statusChangeNote(user);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <StatusChip className="font-semibold" size="sm" variant={userStateChipVariant(user.state)}>
        {formatUserStateTitle(user.state)}
      </StatusChip>
      {changeNote ? <span className="mr-1 text-xs text-muted-foreground">{changeNote}</span> : null}
      <button
        aria-label={`Change segment (${formatSegmentTitle(user.segment)})`}
        className={cn(
          STATUS_CHIP_SIZE_CLASSES.sm,
          'inline-flex items-center gap-1.5 rounded-full border bg-muted font-medium text-foreground transition-colors hover:border-foreground/30 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
        onClick={onEditSegment}
        type="button"
      >
        {formatSegmentTitle(user.segment)}
        <Pencil aria-hidden className="size-3 text-muted-foreground" />
      </button>
      {user.userType !== 'ONE_TRADING_CLIENT' ? (
        <StatusChip size="sm" variant="info">
          {formatUserTypeTitle(user.userType)}
        </StatusChip>
      ) : null}
      {user.broker ? (
        <Link
          className={cn(
            STATUS_CHIP_SIZE_CLASSES.sm,
            'inline-flex items-center rounded-full border font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
          to={`/operations/brokers/${encodeURIComponent(user.broker.id)}`}
        >
          via {user.broker.name}
        </Link>
      ) : null}
      {user.staff ? (
        <StatusChip size="sm" variant="muted">
          Staff
        </StatusChip>
      ) : null}
      <AbuseStatusChip subjectKey={{ subjectType: 'user', subjectId: user.PID }} />
      {restricted.length > 0 ? (
        <StatusChip size="sm" variant="destructive">
          {restricted.join(', ')} off
        </StatusChip>
      ) : null}
      {pendingCount > 0 ? (
        <button className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={onManageAccess} type="button">
          <StatusChip className="hover:bg-amber-500/20" size="sm" icon={CircleAlert} variant="warning">
            {pluralize(pendingCount, 'pending item')}
          </StatusChip>
        </button>
      ) : null}
    </div>
  );
};
UserStatusChips.displayName = 'UserStatusChips';

interface IdentifierProps {
  label: string;
  value: string | null | undefined;
  copyLabel?: string;
  mono?: boolean;
}

const Identifier = ({ label, value, copyLabel, mono = false }: IdentifierProps) => {
  let content: ReactNode = <span className="text-sm text-muted-foreground">not set</span>;
  if (value) {
    content = (
      <span className={cn('inline-flex items-center gap-1', !mono && 'text-sm')}>
        {value}
        {copyLabel ? <CopyButton className="-my-1" label={copyLabel} value={value} /> : null}
      </span>
    );
  }
  return (
    <LabeledValue label={label} mono={mono && Boolean(value)}>
      {content}
    </LabeledValue>
  );
};
Identifier.displayName = 'Identifier';

interface UserHeaderProps {
  user: ConsoleUser;
  onEditSegment: () => void;
  onManageAccess: () => void;
}

interface UserTitleProps {
  user: ConsoleUser;
}

const UserTitle = ({ user }: UserTitleProps) => {
  const { title, email } = userHeading(user);
  const isEmailTitle = title === user.email;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-1">
        <h1 className="break-words text-2xl font-semibold leading-tight tracking-tight">{title}</h1>
        {isEmailTitle ? <CopyButton label="Copy email" value={title} /> : null}
      </div>
      {email ? (
        <p className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
          <span className="break-all">{email}</span>
          <CopyButton className="-my-1" label="Copy email" value={email} />
        </p>
      ) : null}
      {!email && !isEmailTitle && isBrokerClientType(user.userType) ? <p className="text-sm text-muted-foreground">No email · logs in through their broker</p> : null}
    </div>
  );
};
UserTitle.displayName = 'UserTitle';

export const UserHeader = ({ user, onEditSegment, onManageAccess }: UserHeaderProps) => (
  <header className="flex flex-col gap-4 border-b pb-6">
    <div className="flex items-start justify-between gap-4">
      <UserTitle user={user} />
      <UserActionsMenu onEditSegment={onEditSegment} onManageAccess={onManageAccess} user={user} />
    </div>
    <UserStatusChips onEditSegment={onEditSegment} onManageAccess={onManageAccess} user={user} />
    <div className="flex flex-wrap gap-x-10 gap-y-3">
      <Identifier copyLabel="Copy PID" label="PID" mono value={user.PID} />
      <Identifier copyLabel="Copy FAST ID" label="FAST ID" mono value={user.FastId} />
      {/* created is the literal string 'not set' when missing - pass null so it mutes */}
      <Identifier label="Created" value={user.createdAt === undefined ? null : user.created} />
    </div>
  </header>
);
UserHeader.displayName = 'UserHeader';
